/**
 * MembershipSection — PooPartner-facing tier subscription panel.
 *
 * Renders on the provider dashboard. Shows the partner's CURRENT tier
 * (big card with badge + price + commission + perks) and a comparison
 * STRIP listing the other tiers with "Request Upgrade" buttons.
 *
 * Pending-state rules:
 *   - The partner can hold at most ONE pending tier upgrade request at
 *     a time. The server's `find_pending_tier_request` already returns
 *     the most recent pending. We surface that via a yellow pill on the
 *     requested tier's card AND disable the Request Upgrade buttons on
 *     ALL other tiers until the admin resolves.
 *   - The current tier card is read-only — no Request Upgrade button on
 *     itself.
 *
 * What partners CANNOT do (server-enforced):
 *   - Self-grant a tier. The PATCH /api/profiles/provider endpoint
 *     silently drops `tier_id` (extra="ignore"), and tier mutations
 *     only happen via admin approval.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Award, Loader2, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import BadgeChip from "@/components/BadgeChip";
import { api, formatApiErrorDetail } from "@/lib/api";
import ImpersonationConfirmGate, {
  useImpersonationGate,
} from "@/components/ImpersonationConfirmGate";


function TierFee({ tier }) {
  if (tier.fee_type === "flat") {
    return (
      <span className="text-sm font-semibold text-pp-text">
        ${Number(tier.fee_value || 0).toFixed(2)} flat per booking
      </span>
    );
  }
  return (
    <span className="text-sm font-semibold text-pp-text">
      {Number(tier.fee_value || 0).toFixed(0)}% platform fee
    </span>
  );
}


export default function MembershipSection({ providerProfile }) {
  // The provider's current tier_id lives on their provider_profile. The
  // panel pulls the full tier list + own pending requests on mount.
  const currentTierId = providerProfile?.tier_id || null;
  const { requestAction, gateProps } = useImpersonationGate();

  const [tiers, setTiers] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Request Upgrade modal state.
  const [openTier, setOpenTier] = useState(null); // tier doc | null
  const [note, setNote] = useState("");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [tiersRes, requestsRes] = await Promise.all([
        api.get("/profiles/provider/tiers"),
        api.get("/profiles/provider/tier-upgrade-requests"),
      ]);
      setTiers(tiersRes.data || []);
      setMyRequests(requestsRes.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const pendingRequest = useMemo(
    () => myRequests.find((r) => r.status === "pending") || null,
    [myRequests]
  );

  const currentTier = tiers.find((t) => t.id === currentTierId) || null;
  const otherTiers = tiers.filter((t) => t.id !== currentTierId);

  const reallySubmit = async () => {
    if (!openTier) return;
    setSubmitting(true);
    try {
      await api.post(
        "/profiles/provider/tier-upgrade-request",
        {
          requested_tier_id: openTier.id,
          message: note.trim() || null,
        }
      );
      toast.success("Request sent — awaiting admin approval.");
      setOpenTier(null);
      setNote("");
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };
  const submit = () => {
    requestAction("submit tier upgrade request", reallySubmit);
  };

  if (loading && tiers.length === 0) {
    return (
      <section className="py-6 text-center text-pp-text-muted" data-testid="membership-section-loading">
        <Loader2 className="mx-auto h-5 w-5 animate-spin" /> Loading membership…
      </section>
    );
  }

  return (
    <section className="mb-8" data-testid="membership-section">
      <div className="mb-4 flex items-center gap-3">
        <Crown className="h-6 w-6 text-brand-orange" aria-hidden="true" />
        <h2 className="font-heading text-2xl font-bold text-pp-text">
          Your Membership
        </h2>
      </div>

      {/* Current tier card */}
      {currentTier && (
        <Card
          className="mb-5 rounded-2xl border-2 border-brand-orange/40 bg-orange-50/40 dark:bg-orange-950/20"
          data-testid="membership-current-tier-card"
        >
          <CardHeader>
            <CardTitle className="flex flex-wrap items-baseline gap-3">
              <span className="font-heading text-3xl font-bold text-pp-text">
                {currentTier.name}
              </span>
              {currentTier.badge_id && currentTier.badge_id !== "none" && (
                <BadgeChip badgeId={currentTier.badge_id} size="sm" />
              )}
              <span className="ml-auto text-xs uppercase tracking-wider text-pp-text-muted">
                Current tier
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-baseline gap-3">
              <span
                className="font-heading text-4xl font-bold text-pp-text"
                data-testid="membership-current-price"
              >
                ${Number(currentTier.monthly_price || 0).toFixed(0)}
              </span>
              <span className="text-sm font-semibold text-pp-text-muted">/ month</span>
              <span className="ml-2"><TierFee tier={currentTier} /></span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Comparison strip */}
      <div>
        <h3 className="mb-3 font-heading text-sm font-semibold uppercase tracking-wider text-pp-text-muted">
          Other tiers
        </h3>
        <div
          className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
          data-testid="membership-comparison-strip"
        >
          {otherTiers.map((t) => {
            const isPendingTarget =
              pendingRequest && pendingRequest.requested_tier_id === t.id;
            const disabled = !!pendingRequest;
            return (
              <Card
                key={t.id}
                className="rounded-2xl border border-pp-border bg-pp-surface"
                data-testid={`membership-tier-card-${t.id}`}
              >
                <CardHeader>
                  <CardTitle className="flex items-baseline justify-between gap-2">
                    <span className="font-heading text-xl font-semibold text-pp-text">
                      {t.name}
                    </span>
                    {t.badge_id && t.badge_id !== "none" && (
                      <BadgeChip badgeId={t.badge_id} size="xs" />
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="mb-3 text-sm text-pp-text">
                    <span className="font-semibold">${Number(t.monthly_price || 0).toFixed(0)}/mo</span>{" "}
                    · <TierFee tier={t} />
                  </p>
                  {isPendingTarget ? (
                    <p
                      className="rounded-full bg-yellow-100 px-3 py-1 text-center text-xs font-semibold uppercase tracking-wider text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200"
                      data-testid={`membership-pending-pill-${t.id}`}
                    >
                      Request pending — awaiting admin approval
                    </p>
                  ) : (
                    <Button
                      type="button"
                      onClick={() => setOpenTier(t)}
                      disabled={disabled}
                      className="w-full rounded-full bg-pp-orange text-pp-cta-text hover:bg-pp-orange-hover disabled:opacity-50"
                      data-testid={`membership-request-upgrade-${t.id}`}
                    >
                      {disabled ? "Pending request open" : "Request Upgrade"}
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Request Upgrade modal */}
      <Dialog
        open={!!openTier}
        onOpenChange={(open) => !open && !submitting && (setOpenTier(null), setNote(""))}
      >
        <DialogContent className="sm:max-w-md" data-testid="membership-request-modal">
          <DialogHeader>
            <DialogTitle data-testid="membership-request-modal-title">
              Request {openTier?.name} tier
            </DialogTitle>
            <DialogDescription>
              ${Number(openTier?.monthly_price || 0).toFixed(0)}/mo ·{" "}
              {openTier?.fee_type === "flat"
                ? `$${Number(openTier?.fee_value || 0).toFixed(2)} flat per booking`
                : `${Number(openTier?.fee_value || 0).toFixed(0)}% platform fee`}
              . Admin must approve before the tier is updated.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted">
              Add a note for the admin (optional, ≤500 chars)
            </Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 500))}
              placeholder="Tell the admin why you'd like this tier…"
              rows={4}
              data-testid="membership-request-note"
            />
            <p className="text-right text-xs text-pp-text-muted">{note.length}/500</p>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => { setOpenTier(null); setNote(""); }}
              disabled={submitting}
              data-testid="membership-request-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={submit}
              disabled={submitting}
              className="bg-pp-orange text-pp-cta-text hover:bg-pp-orange-hover"
              data-testid="membership-request-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…
                </>
              ) : (
                "Send request"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ImpersonationConfirmGate {...gateProps} />
    </section>
  );
}
